import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import {
  Row,
  Col,
  Select,
} from 'antd';
import {
  CalendarOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowConfigureDrawer from './WorkflowConfigureDrawer';

import { getType, updateData } from './workflowHelpers';
import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';

const HOVER_TEXT = `
Use this step to create a shift and progress the workflow when the assigned user clocks in/out from that shift.
`;

const DISABLED_TEXT = 'Add at least one of each: Text or Dropdown Field, Date/Time Range or a Shift Table to enable this step';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires one of the following to be configured:
      <ul style={{ paddingLeft: 16, marginTop: 8 }}>
        <li>
          <b>Field Mappings</b> (set via <b>Configure</b>):
          <ul style={{ paddingLeft: 16, marginTop: 4 }}>
            <li><b>Title</b></li>
            <li><b>Date/Time</b></li>
          </ul>
        </li>
        <li style={{ marginTop: 8 }}>
          <b>OR</b>
        </li>
        <li>
          Enable <b>Use Shift Table</b> and select a shift table
        </li>
      </ul>
    </div>
  ),
};

const getShiftFields = (t) => [
  { key: 'title', text: 'Title', type: 'text', types: ['dropdown'] },
  { key: 'description', text: 'Description', type: 'text' },
  { key: 'date', text: 'Date/Time', type: 'dateRange' },
  { key: 'userIds', text: 'Assigned User(s)', type: 'dropdown - Users' },
  { key: 'projectId', text: `${t('Project')}`, type: 'dropdown - Projects' },
  { key: 'costcodeId', text: 'Cost Code', type: 'dropdown - Costcodes' },
  { key: 'fileIds', text: 'Attachments', type: 'attachment' },
  { key: 'shiftCategoryId', text: 'Category', type: 'dropdown - ScheduleCategories' },
];

const requiredFields = new Set(['title', 'date']);

const CHECKBOX_STYLE = { paddingBottom: 15 };

export default function WorkflowDispatchNode({
  setElements,
  setDataMap,
  isDisplay,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    disabled,
    data = {},
  }) {
    const { t } = useTranslation();
    const shiftFields = getShiftFields(t);
    const title = `Dispatch${disabled ? ' - DISABLED' : ''}`;
    const {
      fieldMappings: initialFieldMappings = {},
      useMultiShiftField,
      multiShiftFieldId,
      shouldLockClockIn: shouldLockClockInInitial,
      isDraft: isDraftInitial,
      duplicateWorkflow: isDuplicateInitial = true,
      shiftCategoryId,
    } = data;

    const scheduleCategories = useSelector(((state) => state.schedule.categories));

    const [shouldLockClockIn, setShouldLockClockIn] = useState(!!shouldLockClockInInitial);
    const [isDraft, setIsDraft] = useState(!!isDraftInitial);
    const [duplicateWorkflow, setDuplicateWorkflow] = useState(!!isDuplicateInitial);
    const [categoryId, setCategoryId] = useState(shiftCategoryId);

    const onShouldLockClockInChange = useCallback(() => {
      setShouldLockClockIn(!shouldLockClockIn);
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], shouldLockClockIn: !shouldLockClockIn },
      }));
    }, [id, shouldLockClockIn]);

    const onIsDraftChange = useCallback(() => {
      setIsDraft(!isDraft);
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], isDraft: !isDraft },
      }));
    }, [id, isDraft]);

    const onDuplicateChanged = useCallback((newValue) => {
      setDuplicateWorkflow(newValue);
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], duplicateWorkflow: newValue },
      }));
    }, [id]);

    const shiftTables = useMemo(() => (
      sections.map(({ fields = [] }) => (
        fields.filter((field) => {
          const type = getType(field);
          return type === 'table - Shifts';
        })
      ))
        .flat()
        .map((field) => {
          const {
            configProps: { title: fieldTitle } = {},
            id: fieldId,
          } = field;
          return { label: fieldTitle, value: fieldId };
        })
    ), [sections]);

    const enableMultiShift = shiftTables.length > 0;
    const initialMultiShift = enableMultiShift && useMultiShiftField;

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [stateUsingMultiShift, setStateUsingMultiShift] = useState(initialMultiShift);
    const [shiftTableId, setShiftTableId] = useState(multiShiftFieldId);

    const hasValidConfiguration = useMemo(() => {
      if (stateUsingMultiShift) {
        return !!shiftTableId;
      }

      return !!fieldMappings?.title && !!fieldMappings?.date;
    }, [stateUsingMultiShift, shiftTableId, fieldMappings]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: !hasValidConfiguration }));
    }, [hasValidConfiguration, setDisableNext, id]);

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id]);

    const onCheckChanged = useCallback((checked) => {
      setStateUsingMultiShift(checked);
      const newData = { useMultiShiftField: checked };
      if (!checked) newData.multiShiftFieldId = null;
      setDataMap(updateData(id, newData));
      if (checked) {
        // Need to remove outbound edges for multi-shift dispatch
        setElements((elements) => (
          elements.filter((elem) => {
            const { sourceHandle } = elem;
            return !sourceHandle || !sourceHandle.startsWith(id);
          })
        ));
      }
    }, [setDataMap, id]);

    const onShiftTableSelect = useCallback((newFieldId) => {
      if (!setDataMap || !id) return;
      setShiftTableId(newFieldId);
      setDataMap(updateData(id, { multiShiftFieldId: newFieldId }));
    }, [setDataMap, id]);

    const fieldRenderMap = useMemo(() => ({
      shiftCategoryId: ({
        fieldMappings: propFieldMappings,
        sourceFieldMap,
        defaultValue,
        onFieldMappingsChange: propOnFieldMappingsChange,
      }) => {
        const {
          'dropdown - ScheduleCategories': categories = [],
        } = sourceFieldMap;

        const eligibleSourceFields = categories.concat(
          scheduleCategories.map((category) => ({
            id: category.id,
            title: category.name,
          })),
        );

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={propFieldMappings}
            defaultValue={defaultValue}
            onFieldMappingsChange={propOnFieldMappingsChange}
            targetId="shiftCategoryId"
            eligibleSourceFields={eligibleSourceFields}
          />
        );
      },
    }), [scheduleCategories]);

    const categoryOptions = useMemo(() => {
      const baseCategories = scheduleCategories.map((category) => ({
        id: category.id,
        title: category.name,
      }));
      const fieldScheduleCategories = sections.map(({ fields = [] }) => (
        fields.filter((field) => {
          const type = getType(field);
          return type === 'dropdown - ScheduleCategories';
        })
      ))
        .flat()
        .map((field) => {
          const {
            configProps: { title: fieldTitle } = {},
            id: fieldId,
          } = field;
          return { id: fieldId, title: fieldTitle };
        });

      return baseCategories.concat(fieldScheduleCategories).map((category) => ({
        label: category.title,
        value: category.id,
      }));
    }, [
      sections,
      scheduleCategories,
    ]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={CalendarOutlined}
        type="dispatch"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={!hasValidConfiguration ? ERROR_DETAILS : undefined}
      >
        {!draggable && (
        <div>
          {enableMultiShift && (
            <OnTraccrCheckbox
              onChange={onCheckChanged}
              defaultChecked={enableMultiShift && useMultiShiftField}
              value={stateUsingMultiShift && enableMultiShift}
              label="Use Shift Table?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want to link this dispatch step to a Shift table.<br /> <br />
                  Leave this box unchecked if you want to link this dispatch step to individual fields.
                </div>
              )}
            />
          )}
          <Row style={{ margin: '20px 0px' }}>
            {stateUsingMultiShift
              ? (
                <Select
                  options={shiftTables}
                  optionFilterProp="label"
                  value={shiftTableId}
                  onChange={onShiftTableSelect}
                />
              )
              : (
                <BorderlessButton
                  title="Configure"
                  style={{ margin: 5 }}
                  iconNode={<SettingOutlined />}
                  onClick={openDrawer}
                />
              )}
          </Row>
          {!!stateUsingMultiShift && (
            <Row style={{ marginBottom: 20 }}>
              <Select
                style={{ width: '100%' }}
                options={categoryOptions}
                optionFilterProp="label"
                value={categoryId}
                onChange={(newValue) => {
                  setCategoryId(newValue);
                  setDataMap((dataMap) => ({
                    ...dataMap,
                    [id]: {
                      ...dataMap[id],
                      shiftCategoryId: newValue,
                    },
                  }));
                }}
                placeholder="Select a Category"
                showSearch
                allowClear
              />
            </Row>
          )}
          <OnTraccrCheckbox
            value={shouldLockClockIn}
            onChange={onShouldLockClockInChange}
            style={CHECKBOX_STYLE}
            label="Lock Clock In Details"
          />
          <OnTraccrCheckbox
            value={isDraft}
            onChange={onIsDraftChange}
            style={CHECKBOX_STYLE}
            label="Create as Draft"
          />
          <OnTraccrCheckbox
            checkboxStyle={{ marginRight: 5 }}
            style={{ marginBottom: 15 }}
            label='Duplicate Workflows'
            value={duplicateWorkflow}
            onChange={onDuplicateChanged}
            hoverNode={(
              <div style={{ maxWidth: 300 }}>
                <strong>Using Shift Table:</strong>
                <p>
                  Check this box if you want to create a new workflow on clock in/out for each shift
                  created from this dispatch step.
                </p>

                <strong>Using Field Mappings:</strong>
                <p>
                  Check this box if you want to create a new workflow on clock in/out for each user
                  assigned to the created shift.
                </p>

                <p>
                  <strong>Note:</strong>
                  {' '}
                  Leave this box unchecked if you want the workflow to be shared by all shift users.
                </p>
              </div>
            )}
          />
          <Row justify="space-between" style={{ marginBottom: 5 }}>
            <Col>
              Clock In
            </Col>
            <Col>
              Clock Out
            </Col>
          </Row>
          <WorkflowHandle
            type="source"
            position="bottom"
            style={{ left: '20%' }}
            id={`${id}-clockin`}
            disabled={isDisplay}
          />
          <WorkflowHandle
            type="source"
            position="bottom"
            style={{ left: '80%' }}
            id={`${id}-clockout`}
            disabled={isDisplay}
          />
          <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          <WorkflowConfigureDrawer
            id={id}
            visible={showDrawer}
            onClose={closeDrawer}
            onSubmit={closeDrawer}
            sourceName={name}
            sourceSections={sections}
            fieldMappings={fieldMappings}
            onFieldMappingsChange={onFieldMappingsChange}
            data={data}
            setDataMap={setDataMap}
            fields={shiftFields}
            requiredFields={requiredFields}
            fieldRenderMap={fieldRenderMap}
            mapDropdown
          />
        </div>
        )}
      </WorkflowActionNode>
    );
  };
}
